import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  AUDIT_ACTION_LABELS,
  AUDIT_ENTITY_LABELS,
  summarizeAuditChange,
} from '@/services/audit/labels';
import { listAuditLogs } from '@/services/audit';
import { modulePaths } from '@/routes/paths';
import type { AuditAction, AuditEntityType, AuditLog } from '@/types/audit';

const ENTITY_FILTER_OPTIONS: AuditEntityType[] = [
  'user',
  'category',
  'subcategory',
  'customFieldDefinition',
  'inventoryItem',
  'reservation',
  'client',
  'commercialOperation',
  'logisticsOrder',
  'logisticsProvider',
  'maintenanceTask',
  'inventoryMovement',
  'inventoryLocation',
  'invoice',
  'system',
];

const ACTION_FILTER_OPTIONS: AuditAction[] = [
  'create',
  'update',
  'status_change',
  'role_change',
  'permission_denied',
  'login',
  'logout',
];

function formatWhen(date: Date): string {
  return date.toLocaleString('es-AR', {
    dateStyle: 'short',
    timeStyle: 'short',
  });
}

export function AuditActivityPage() {
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [entityType, setEntityType] = useState<AuditEntityType | ''>('');
  const [action, setAction] = useState<AuditAction | ''>('');
  const [actorUid, setActorUid] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [search, setSearch] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const list = await listAuditLogs({
        ...(entityType ? { entityType } : {}),
        ...(action ? { action } : {}),
        ...(actorUid.trim() ? { actorUid: actorUid.trim() } : {}),
        ...(fromDate ? { fromDate } : {}),
        ...(toDate ? { toDate } : {}),
        ...(search.trim() ? { search: search.trim() } : {}),
        limit: 200,
      });
      setLogs(list);
    } catch {
      setError('No se pudo cargar la actividad. Verificá permisos y reglas de auditLogs.');
    } finally {
      setLoading(false);
    }
  }, [entityType, action, actorUid, fromDate, toDate, search]);

  useEffect(() => {
    void load();
  }, [load]);

  const actorOptions = useMemo(() => {
    const map = new Map<string, string>();
    for (const log of logs) {
      if (!map.has(log.actorUid)) {
        map.set(log.actorUid, log.actorEmail ?? log.actorUid);
      }
    }
    return [...map.entries()].sort((a, b) => a[1].localeCompare(b[1]));
  }, [logs]);

  return (
    <section>
      <p>
        <Link to={modulePaths.admin}>← Administración</Link>
      </p>
      <h1>Actividad y auditoría</h1>
      <p className="auth-muted">
        Registro operativo interno. Solo visible para administradores. Los logs
        no son editables desde la UI.
      </p>

      <form
        className="inventory-panel audit-filters"
        onSubmit={(e) => {
          e.preventDefault();
          void load();
        }}
      >
        <div className="users-form-grid">
          <label className="auth-label">
            Entidad
            <select
              value={entityType}
              onChange={(e) =>
                setEntityType(e.target.value as AuditEntityType | '')
              }
            >
              <option value="">Todas</option>
              {ENTITY_FILTER_OPTIONS.map((t) => (
                <option key={t} value={t}>
                  {AUDIT_ENTITY_LABELS[t]}
                </option>
              ))}
            </select>
          </label>
          <label className="auth-label">
            Acción
            <select
              value={action}
              onChange={(e) => setAction(e.target.value as AuditAction | '')}
            >
              <option value="">Todas</option>
              {ACTION_FILTER_OPTIONS.map((a) => (
                <option key={a} value={a}>
                  {AUDIT_ACTION_LABELS[a]}
                </option>
              ))}
            </select>
          </label>
          <label className="auth-label">
            Usuario (UID)
            <input
              list="audit-actors"
              value={actorUid}
              onChange={(e) => setActorUid(e.target.value)}
              placeholder="UID o seleccionar"
            />
            <datalist id="audit-actors">
              {actorOptions.map(([uid, label]) => (
                <option key={uid} value={uid}>
                  {label}
                </option>
              ))}
            </datalist>
          </label>
          <label className="auth-label">
            Desde
            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
            />
          </label>
          <label className="auth-label">
            Hasta
            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
            />
          </label>
          <label className="auth-label">
            Búsqueda
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="email, entityId, metadata…"
            />
          </label>
        </div>
        <div className="inventory-actions">
          <button type="submit" className="auth-button">
            Aplicar filtros
          </button>
        </div>
      </form>

      {loading ? <p className="auth-muted">Cargando…</p> : null}
      {error ? <p className="auth-error">{error}</p> : null}

      <table className="users-table">
        <thead>
          <tr>
            <th>Fecha</th>
            <th>Usuario</th>
            <th>Acción</th>
            <th>Entidad</th>
            <th>ID</th>
            <th>Resumen</th>
          </tr>
        </thead>
        <tbody>
          {logs.map((log) => (
            <tr key={log.id}>
              <td>{formatWhen(log.createdAt)}</td>
              <td>{log.actorEmail ?? log.actorUid}</td>
              <td>{AUDIT_ACTION_LABELS[log.action]}</td>
              <td>{AUDIT_ENTITY_LABELS[log.entityType]}</td>
              <td>
                <code>{log.entityId}</code>
              </td>
              <td>{summarizeAuditChange(log)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {!loading && logs.length === 0 ? (
        <p className="auth-muted">No hay registros con los filtros actuales.</p>
      ) : null}
    </section>
  );
}
